import React, { useEffect, useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { followService } from "../services/resources.service";
import { formatFollowerCount, getMessage, useLocale } from "../i18n/locale";

export function includesFollow(items, subjectType, subjectId) {
  return items.some(
    (item) => item.subjectType === subjectType && String(item.subjectId) === String(subjectId)
  );
}

export default function FollowButton({
  subjectType,
  subjectId,
  user,
  redirectTo,
  onRequireAuth,
  onFeedback,
  followerCount = 0,
}) {
  const { locale } = useLocale();
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(Boolean(user));
  const [count, setCount] = useState(Math.max(0, Number(followerCount) || 0));

  useEffect(() => {
    setCount(Math.max(0, Number(followerCount) || 0));
  }, [followerCount, subjectId]);

  useEffect(() => {
    let active = true;
    if (!user || !subjectId) {
      setFollowing(false);
      setLoading(false);
      return () => { active = false; };
    }
    setLoading(true);
    followService.list()
      .then((items) => {
        if (active) {
          setFollowing(includesFollow(items, subjectType, subjectId));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectId, subjectType, user]);

  const toggle = async () => {
    if (!user) {
      onRequireAuth?.({ redirectTo, reasonKey: "loginModalFollowReason" });
      return;
    }
    if (loading) return;
    const previous = following;
    setFollowing(!previous);
    setCount((current) => Math.max(0, current + (previous ? -1 : 1)));
    setLoading(true);
    try {
      if (previous) await followService.remove(subjectType, subjectId);
      else await followService.create(subjectType, subjectId);
      onFeedback?.(getMessage(locale, previous ? "followRemoved" : "followAdded"), "success");
    } catch {
      setFollowing(previous);
      setCount((current) => Math.max(0, current + (previous ? 1 : -1)));
      onFeedback?.(getMessage(locale, "followError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const label = getMessage(locale, following ? "unfollow" : "follow");
  const countLabel = formatFollowerCount(count, locale);
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
      <Tooltip title={label}>
        <span>
          <IconButton onClick={toggle} aria-label={label} disabled={loading && Boolean(user)}>
            {loading && user
              ? <CircularProgress size={20} />
              : following ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
        {countLabel}
      </Typography>
    </Box>
  );
}
